/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2023 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import * as React from "react";
import { useEffect, useRef } from "react";
import styled, { css, SimpleInterpolation } from "styled-components";

import { Portal } from "react-portal";
import { CheckoutStatus, useCheckout } from "hooks/useCheckout";
import { CommerceInfo } from "components/EnhancedSpeech/Utilities";
import { devices, useIsMobile } from "components/shared/Styles";
import { isMobileSafari } from "utilities/BrowserUtilities";
import { ThankYou } from "components/ThankYou";
import { ExpressCheckoutButtonPosition } from "constants/Analytics";
import { useCurrentUser } from "actions/CurrentUserActions";
import { OverlayTrigger } from "@spectrum-web-components/overlay-trigger";
import { useEventListener } from "hooks/useEventListener";

const BackgroundFog = styled.div`
  position: fixed;
  width: 100vw;
  height: 100vh;
  top: 0;
  left: 0;
  background-color: rgba(
    var(--spectrum-global-color-static-indigo-600-rgb),
    0.7
  );
  z-index: 6; // to overcome app header z-index of 5
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
`;

const CheckoutIFrame = styled.iframe<{ hidden: boolean }>`
  margin-top: 30px;
  width: 75%;
  height: 90%;
  overflow: hidden;

  ${devices.small} {
    width: 100%;
    height: ${isMobileSafari() ? "90lvh" : "100lvh"};
    margin: 5px auto auto;
  }

  ${({ hidden }): SimpleInterpolation => {
    if (hidden) {
      return css`
        display: none;
      `;
    }
  }})
`;

// This is a workaround to add overflow wrap break words behavior to the label text content of the sp-button
const ButtonLabelText = styled.div`
  display: flex;
  gap: 10px;
  justify-content: center;
  align-items: center;
  ${devices.small} {
    white-space: normal;
  }
`;

const CheckoutView: React.FC<{
  commerceInfo: CommerceInfo | null;
  checkoutStatus: CheckoutStatus;
  closeCheckout: () => void;
}> = ({ commerceInfo, checkoutStatus, closeCheckout }) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    if (checkoutStatus === CheckoutStatus.LOADED && iframeRef.current) {
      iframeRef.current.focus();
    }
  }, [checkoutStatus]);

  if (checkoutStatus === CheckoutStatus.CLOSED) {
    return <></>;
  }
  return (
    <BackgroundFog>
      {commerceInfo?.url && (
        <CheckoutIFrame
          ref={iframeRef}
          id="checkout-frame"
          hidden={
            checkoutStatus === CheckoutStatus.LOADING ||
            checkoutStatus === CheckoutStatus.THANK_YOU
          }
          src={commerceInfo!.url}
          title={I18n.t("checkout.iframe_title")}
          aria-modal="true"
          role="dialog"
          aria-label={I18n.t("checkout.iframe_title")}
        />
      )}
      {checkoutStatus === CheckoutStatus.LOADING && (
        <sp-progress-circle
          size="l"
          indeterminate
          aria-alt={I18n.t("loading")}
        />
      )}
      {checkoutStatus === CheckoutStatus.THANK_YOU && (
        <ThankYou closeCheckout={closeCheckout} />
      )}
    </BackgroundFog>
  );
};

export const PurchaseExpressButton: React.FC<{
  color?: "white" | "black" | undefined;
  position: ExpressCheckoutButtonPosition;
  onClick?: VoidFunction;
  onClosed?: VoidFunction;
  focusOnClose?: boolean;
  size?: "xl" | "l";
}> = ({
  color,
  position,
  onClick,
  onClosed,
  focusOnClose = false,
  children,
  size = "l",
}) => {
  return (
    <UpgradeExpressButton
      color={color}
      onClick={onClick}
      onClosed={onClosed}
      position={position}
      buttonSize={size}
      buttonStyle={{
        paddingLeft: "10px",
      }}
      focusOnClose={focusOnClose}
    >
      <ButtonLabelText>{children}</ButtonLabelText>
    </UpgradeExpressButton>
  );
};

export const PurchaseExpressClickableDiv = React.forwardRef<
  HTMLDivElement,
  {
    position: ExpressCheckoutButtonPosition;
    ariaDescription?: string;
    children: React.ReactNode;
  }
>(({ position, ariaDescription, children }, ref) => {
  const { checkoutStatus, commerceInfo, openCheckout, closeCheckout } =
    useCheckout(position);

  const hasPremiumAccess =
    useCurrentUser().currentUser?.hasPremiumAccess ?? false;

  const props = !hasPremiumAccess
    ? {
        "aria-label": `${I18n.t("bento_box.upgrade_to_premium")} ${ariaDescription ?? ""}`,
        style: { cursor: "pointer", background: "transparent" },
        onClick: (): Promise<void> => openCheckout(),
        role: "button",
      }
    : {};

  return (
    <>
      <div ref={ref} {...props}>
        {children}
      </div>
      <Portal node={document && document.getElementById("root")}>
        <CheckoutView
          checkoutStatus={checkoutStatus}
          commerceInfo={commerceInfo}
          closeCheckout={closeCheckout}
        />
      </Portal>
    </>
  );
});

export const UpgradeExpressButton: React.FC<{
  color?: "white" | "black";
  onClick?: VoidFunction;
  onClosed?: VoidFunction;
  focusOnClose?: boolean;
  position: ExpressCheckoutButtonPosition;
  buttonStyle?: React.CSSProperties;
  buttonSize?: "xl" | "l";
}> = ({
  color,
  position,
  children,
  onClick,
  onClosed,
  focusOnClose = false,
  buttonStyle,
  buttonSize,
}) => {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const overlayRef = useRef<OverlayTrigger>(null);
  const { checkoutStatus, commerceInfo, openCheckout, closeCheckout } =
    useCheckout(position);
  const isMobile = useIsMobile();
  let calculatedButtonSize = buttonSize;
  calculatedButtonSize ??= isMobile ? "xl" : "l";

  const hasBeenOpenedRef = useRef(false);

  useEventListener(
    "sp-closed",
    () => {
      closeCheckout();
    },
    () => overlayRef.current
  );

  useEffect(() => {
    if (checkoutStatus !== CheckoutStatus.CLOSED) {
      hasBeenOpenedRef.current = true;
    } else if (
      hasBeenOpenedRef.current &&
      checkoutStatus === CheckoutStatus.CLOSED
    ) {
      if (focusOnClose) {
        buttonRef.current?.focus();
      }

      onClosed?.();
    }
  }, [checkoutStatus, focusOnClose, onClosed]);

  const onClickThenCheckout = (): Promise<void> => {
    onClick?.();
    return openCheckout();
  };

  return (
    <overlay-trigger
      ref={overlayRef}
      type="modal"
      open={checkoutStatus !== CheckoutStatus.CLOSED ? "click" : undefined}
    >
      <sp-button
        ref={buttonRef}
        size={calculatedButtonSize}
        static={color}
        onClick={onClickThenCheckout}
        style={buttonStyle}
        slot="trigger"
      >
        {children}
      </sp-button>
      <div slot="click-content">
        <CheckoutView
          checkoutStatus={checkoutStatus}
          commerceInfo={commerceInfo}
          closeCheckout={closeCheckout}
        />
      </div>
    </overlay-trigger>
  );
};

// TODO: Migrate all upgrade buttons to use this component.
// Right now it is only used for the Studio Filter Upsells.
export const UpgradeExpressButtonForOverlays: React.FC<{
  color?: "white" | "black";
  onClick?: VoidFunction;
  position: ExpressCheckoutButtonPosition;
  buttonStyle?: React.CSSProperties;
  buttonSize?: "xl" | "l";
}> = ({ color, position, children, onClick, buttonStyle, buttonSize }) => {
  const { checkoutStatus, commerceInfo, openCheckout, closeCheckout } =
    useCheckout(position);
  const isMobile = useIsMobile();
  let calculatedButtonSize = buttonSize;
  calculatedButtonSize ??= isMobile ? "xl" : "l";

  const onClickThenCheckout = (): Promise<void> => {
    onClick?.();
    return openCheckout();
  };

  return (
    <overlay-trigger>
      <sp-button
        size={calculatedButtonSize}
        static={color}
        onClick={onClickThenCheckout}
        style={buttonStyle}
        slot="trigger"
      >
        {children}
      </sp-button>
      <div slot="click-content">
        <CheckoutView
          checkoutStatus={checkoutStatus}
          commerceInfo={commerceInfo}
          closeCheckout={closeCheckout}
        />
      </div>
    </overlay-trigger>
  );
};
